import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaImage } from 'react-icons/fa';
import { CommunityFeatures, CommunityMember, CommunityRole } from '../../../../types/misc';
import * as communityService from '../../../../services/communityService';
import { taskService } from '../../../../services/taskService';
import { getCommunityUpcomingEvents } from '../../../../services/eventService';
import photoAlbumService from '../../../../services/photoAlbumService';
import { getAllScoreCards, ScoreCard } from '../../../../services/scoreCardService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import Avatar from '../../../../components/misc/Avatar';
import EventCard from '../../../../components/misc/EventCard';
import { Task } from '../../../../types/task';
import { Event } from '../../../../types/event';
import { PhotoAlbum } from '../../../../types/photoAlbum';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import { DEFAULT_SCORE_CARD_TYPE, formatScore, getScoreCardTypeLabelKey } from '../../../../utils/scoreCardUtil';
import HorizontalSponsorsList from '../../../../components/misc/HorizontalSponsorsList';

interface FeatureCardProps {
  title: string;
  onClick: () => void;
  children?: React.ReactNode;
}

function FeatureCard({ title, onClick, children }: FeatureCardProps) {
  const { t } = useTranslation();

  return (
    <div className="w-full rounded-xl border">
      <div className="flex items-center justify-between rounded-t-xl bg-secondary-200 px-4 py-2">
        <div>
          <h3 className="font-semibold">{title}</h3>
        </div>
        <p onClick={onClick} className="flex cursor-pointer items-center gap-2 text-sm font-medium text-details/60">
          {t('general.view')}
        </p>
      </div>

      {children && <div className="overflow-hidden rounded-b-xl p-4">{children}</div>}
    </div>
  );
}

function EventsFeatureCard({ events, onClick }: { events: Event[]; onClick: () => void }) {
  const { t } = useTranslation();
  const upcomingEvents = events.slice(0, 3);

  return (
    <FeatureCard title={t('page.community.tabs.events')} onClick={onClick}>
      {upcomingEvents.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm">{t('page.dashboard.events.noEvents')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {upcomingEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </FeatureCard>
  );
}

// Feature Card Component for Photo Albums
function AlbumsFeatureCard({ albums, onClick }: { albums: PhotoAlbum[]; onClick: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recentAlbums = albums.slice(0, 4);

  return (
    <FeatureCard title={t('page.community.tabs.albums')} onClick={onClick}>
      {recentAlbums.length === 0 ? (
        <div className="text-secondary-500 py-6 text-center">
          <p className="text-sm">{t('page.dashboard.albums.noAlbums')}</p>
        </div>
      ) : (
        <div>
          <div className="flex gap-3 overflow-x-auto">
            {recentAlbums.map((album) => (
              <button
                key={album.id}
                type="button"
                onClick={() => navigate(`/community/albums/${album.id}`)}
                className="w-28 flex-shrink-0 rounded-lg bg-secondary-50 p-2 text-left transition-shadow hover:shadow"
                aria-label={album.title}>
                {album.coverUrl ? (
                  <img src={album.coverUrl} alt={album.title} className="h-20 w-full rounded-md object-cover" />
                ) : (
                  <div className="flex h-20 w-full items-center justify-center rounded-md bg-secondary-200">
                    <FaImage className="h-5 w-5 text-primary" />
                  </div>
                )}

                <p className="text-secondary-900 mt-2 line-clamp-1 text-sm font-medium">{album.title}</p>
                <p className="text-secondary-600 text-xs">
                  {album.photoCount} {t('general.photo', { count: album.photoCount })}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </FeatureCard>
  );
}

// Feature Card Component for Tasks
function TasksFeatureCard({ tasks, onClick }: { tasks: Task[]; onClick: () => void }) {
  const { t } = useTranslation();
  const recentTasks = tasks.slice(0, 3);

  return (
    <FeatureCard title={t('page.community.tabs.tasks')} onClick={onClick}>
      {recentTasks.length === 0 ? (
        <div className="text-secondary-500 px-4 py-6 text-center">
          <p className="text-sm">{t('page.dashboard.tasks.noOpenTasks')}</p>
        </div>
      ) : (
        <>
          {/* Mobile list */}
          <ul className="flex flex-col gap-2 md:hidden">
            {recentTasks.map((task) => (
              <li
                key={task.taskId}
                className="flex items-center justify-between gap-3 rounded-lg bg-secondary-50 px-3 py-2 transition hover:shadow">
                <div className="flex min-w-0 items-center gap-3">
                  {task.status && (
                    <span
                      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: task.status.color }}
                    />
                  )}
                  <p className="truncate text-sm font-medium text-primary">{task.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {task.assignees && task.assignees.length > 0 ? (
                    <div className="flex -space-x-2">
                      {task.assignees.slice(0, 2).map((assignee) => (
                        <Avatar
                          key={assignee.userId}
                          src={undefined}
                          alias={`${assignee.firstName} ${assignee.lastName}`}
                          className="h-6 w-6 border-2"
                        />
                      ))}
                      {task.assignees.length > 2 && (
                        <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 bg-secondary-200 text-xs">
                          +{task.assignees.length - 2}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-secondary-400 text-xs">—</span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full">
              <thead className="border-b border-details/10 bg-secondary">
                <tr>
                  <th className="w-10 px-4 py-2">
                    <span className="sr-only">{t('page.dashboard.tasks.status')}</span>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-primary">
                    {t('page.dashboard.tasks.description')}
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-primary">
                    {t('page.dashboard.tasks.assignedTo')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-details/10">
                {recentTasks.map((task) => (
                  <tr key={task.taskId} className="hover:bg-secondary-100 transition-colors">
                    <td className="px-4 py-3">
                      {task.status && (
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: task.status.color }}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-primary">{task.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      {task.assignees && task.assignees.length > 0 ? (
                        <div className="flex -space-x-2">
                          {task.assignees.slice(0, 2).map((assignee) => (
                            <Avatar
                              key={assignee.userId}
                              src={undefined}
                              alias={`${assignee.firstName} ${assignee.lastName}`}
                              className="h-6 w-6 border-2"
                            />
                          ))}
                          {task.assignees.length > 2 && (
                            <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 bg-secondary-200 text-xs">
                              +{task.assignees.length - 2}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-secondary-400 text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </FeatureCard>
  );
}

// Feature Card Component for Scores
function ScoresFeatureCard({ scoreCards, onClick }: { scoreCards: ScoreCard[]; onClick: () => void }) {
  const { t } = useTranslation();
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const selectedCard = scoreCards[selectedCardIndex];

  const topScores = selectedCard
    ? [...(selectedCard.scores || [])]
        .sort((a, b) => (selectedCard.ascending ? a.score - b.score : b.score - a.score))
        .slice(0, 3)
    : [];

  return (
    <FeatureCard title={t('page.community.tabs.scores')} onClick={onClick}>
      {/* Score Card Switcher */}
      {scoreCards.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-3">
          {scoreCards.map((card, index) => (
            <button
              key={card.scoreCardId}
              type="button"
              onClick={() => setSelectedCardIndex(index)}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all ${
                selectedCardIndex === index
                  ? 'bg-primary text-secondary'
                  : 'bg-secondary-50 text-primary hover:bg-secondary-200'
              }`}>
              {card.name} · {t(`page.community.scores.forms.type.${getScoreCardTypeLabelKey(card.type)}`)}
            </button>
          ))}
        </div>
      )}
      {selectedCard ? (
        <>
          {/* Mobile list */}
          <ul className="flex flex-col gap-2 md:hidden">
            {topScores.map((score, idx) => (
              <li
                key={score.memberId}
                className="flex items-center justify-between gap-3 rounded-lg bg-secondary-50 px-3 py-2 transition hover:shadow">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex w-6 items-center justify-center text-xs font-semibold text-primary">
                    {idx + 1}
                  </span>

                  <Avatar
                    src={score.picture}
                    alias={`${score.firstName || ''} ${score.lastName || ''}`}
                    className="h-8 w-8"
                  />
                  <span className="truncate text-sm font-medium text-primary">
                    {score.firstName} {score.lastName}
                  </span>
                </div>
                <div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    {formatScore(score.score, selectedCard.type ?? DEFAULT_SCORE_CARD_TYPE, t)}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[420px]">
              <thead className="border-b border-details/10 bg-secondary">
                <tr>
                  <th className="w-10 px-4 py-2 text-left text-sm font-semibold text-primary">#</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-primary">
                    {t('page.community.scores.name')}
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-primary">
                    {t('page.community.scores.scoreNumber')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-details/10">
                {topScores.map((score, idx) => (
                  <tr key={score.memberId} className="hover:bg-secondary-100 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-primary">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={score.picture}
                          alias={`${score.firstName || ''} ${score.lastName || ''}`}
                          className="h-8 w-8"
                        />
                        <span className="text-sm font-medium text-primary">
                          {score.firstName} {score.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-primary">
                        {formatScore(score.score, selectedCard.type ?? DEFAULT_SCORE_CARD_TYPE, t)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-secondary-500 px-4 py-6 text-center">
          <p className="text-sm">{t('page.community.scores.noScoreCards')}</p>
        </div>
      )}
    </FeatureCard>
  );
}

// Feature Card Component for Finances
function FinancesFeatureCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <FeatureCard title={t('page.community.tabs.finances')} onClick={onClick}>
      <div className="text-secondary-500 py-6 text-center">
        <p className="text-sm">{t('page.dashboard.finances.noData')}</p>
      </div>
    </FeatureCard>
  );
}

// Members Sidebar Component
function MembersSidebar({
  members,
  loading,
  searchQuery,
  onSearchChange,
}: {
  members: CommunityMember[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}) {
  const { t } = useTranslation();

  const filteredMembers = members.filter((member) => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="border-secondary-100 flex min-w-0 basis-full flex-col rounded-xl border bg-secondary md:basis-[25%]">
      {/* Header */}
      <div className="rounded-t-xl bg-secondary-200 px-4 py-3">
        <h2 className="text-secondary-900 font-semibold">{t('page.dashboard.community.members')}</h2>
        <p className="text-secondary-600 text-sm">
          {members.length} {t('general.member', { count: members.length })}
        </p>
      </div>

      {/* Search */}
      <div className="border-secondary-100 border-b">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('general.search')}
            className="w-full !rounded-none border !bg-secondary py-2 pl-9 pr-3 text-sm focus:border-none focus:ring-0"
          />
        </div>
      </div>

      {/* Members List */}
      <div className="p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-secondary-500 text-sm">{t('general.loading')}</p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-secondary-500 text-sm">
              {searchQuery ? t('general.noResults') : t('page.dashboard.community.noMembers')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {filteredMembers.map((member) => (
              <div key={member.id} className="flex items-center gap-3 rounded-lg px-3 py-2">
                <Avatar src={member.picture} alias={`${member.firstName} ${member.lastName}`} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <p className="text-secondary-900 truncate text-sm font-medium">
                    {member.firstName} {member.lastName}
                  </p>
                  {member.role === CommunityRole.ADMIN && (
                    <span className="text-xs text-primary">{t('page.dashboard.communities.admin')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to get enabled features
function getEnabledFeatures(features?: CommunityFeatures): string[] {
  if (!features) return [];
  return Object.entries(features)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);
}

export default function CommunityOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedCommunity = useSelectedCommunity();

  const [membersLoading, setMembersLoading] = useState(false);
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Feature data states
  const [tasks, setTasks] = useState<Task[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [albums, setAlbums] = useState<PhotoAlbum[]>([]);
  const [scoreCards, setScoreCards] = useState<ScoreCard[]>([]);

  useEffect(() => {
    if (selectedCommunity?.id) {
      setMembersLoading(true);
      communityService
        .getCommunityMembers(selectedCommunity.id, 50)
        .then((data) => setMembers(data))
        .catch((error) => console.error('Failed to load members:', error))
        .finally(() => setMembersLoading(false));
    }
  }, [selectedCommunity?.id]);

  useEffect(() => {
    if (selectedCommunity?.id && selectedCommunity.features) {
      const fetchPromises: Promise<any>[] = [];

      if (selectedCommunity.features.TASKS) {
        fetchPromises.push(
          taskService
            .getAllTasksForCommunity(selectedCommunity.id)
            .then(setTasks)
            .catch((error) => console.error('Failed to load tasks:', error)),
        );
      }

      if (selectedCommunity.features.EVENTS) {
        fetchPromises.push(
          getCommunityUpcomingEvents(selectedCommunity.id)
            .then(setEvents)
            .catch((error) => console.error('Failed to load events:', error)),
        );
      }

      if (selectedCommunity.features.PHOTO_ALBUMS) {
        fetchPromises.push(
          photoAlbumService
            .getPhotoAlbums(selectedCommunity.id)
            .then(setAlbums)
            .catch((error) => console.error('Failed to load albums:', error)),
        );
      }

      if (selectedCommunity.features.STATS_AND_SCORES) {
        fetchPromises.push(
          getAllScoreCards(selectedCommunity.id)
            .then(setScoreCards)
            .catch((error) => console.error('Failed to load score cards:', error)),
        );
      }
    }
  }, [selectedCommunity?.id, selectedCommunity?.features]);

  if (!selectedCommunity) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  const enabledFeatures = getEnabledFeatures(selectedCommunity.features);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <CommunityHeader title={selectedCommunity.name} />
      {/* Banner: Admins with wanted dashboard features but no dashboard yet */}
      {selectedCommunity.role === CommunityRole.ADMIN &&
        !selectedCommunity.dashboard &&
        (selectedCommunity as any).wantedDashboardFeatures && (
          <div className="mb-4 flex items-start justify-between gap-4 rounded-lg border bg-secondary-50 p-4">
            <div>
              <h3 className="font-semibold text-primary">{t('page.community.dashboardPending.title')}</h3>
              <p className="mt-1 text-details/70">{t('page.community.dashboardPending.description')}</p>
            </div>

            <div className="flex-shrink-0">
              <a
                href="https://www.veracommunity.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90">
                {t('page.community.dashboardPending.manage')}
              </a>
            </div>
          </div>
        )}

      {/* Main Content - Feature Overview */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <div className="flex min-w-0 basis-full flex-col gap-6 md:basis-[75%]">
            <p className="pb-6">{selectedCommunity.description}</p>

            {/* Conditionally show feature cards based on enabled features */}
            {enabledFeatures.includes('EVENTS') && (
              <EventsFeatureCard events={events} onClick={() => navigate('/community/events')} />
            )}

            <HorizontalSponsorsList placement="HOME" />

            {enabledFeatures.includes('PHOTO_ALBUMS') && (
              <AlbumsFeatureCard albums={albums} onClick={() => navigate('/community/albums')} />
            )}

            {enabledFeatures.includes('TASKS') && (
              <TasksFeatureCard tasks={tasks} onClick={() => navigate('/community/tasks')} />
            )}

            {enabledFeatures.includes('STATS_AND_SCORES') && (
              <ScoresFeatureCard scoreCards={scoreCards} onClick={() => navigate('/community/scores')} />
            )}

            {enabledFeatures.includes('COST_SPLITTING') && (
              <FinancesFeatureCard onClick={() => navigate('/community/finances')} />
            )}
          </div>

          {/* Members Sidebar */}
          <MembersSidebar
            members={members}
            loading={membersLoading}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
      </div>
    </div>
  );
}
